import { useMemo, useState } from 'react';
import { doApiCheck, UserKeys } from '../../../api/users-api';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import AvatarChip from '../../../shared/avatar-chip';
import { useUserSettings } from '../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../utils/feature-flags';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Check from '@mui/icons-material/Check';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Error from '@mui/icons-material/Error';
import WarningAmber from '@mui/icons-material/WarningAmber';
import SharedTabHeader from '../../../shared/shared-tab-header';

const ADMIN_API_SETTINGS = {
  activity: 'Activity Reports',
  emailAudit: 'Email Audits',
  groups: 'Groups',
  groupSettings: 'Group Settings',
  mobile: 'Mobile Devices',
  orgs: 'Organizations',
  security: 'Security',
  usage: 'Usage Reports',
  users: 'Users',
  chromeManagement: 'Chrome Management API',
  cloudIdentity: 'Cloud Identity API',
  vault: 'Google Vault',
  driveLabels: 'Drive Labels',
};

// Only checked when the Drive Labels ops feature is enabled for the domain.
const DRIVE_LABELS_API_SETTINGS = {
  driveLabelsWrite: 'Drive Labels (Manage)',
};

const USER_API_SETTINGS = {
  calendars: 'Calendars',
  contacts: 'Contacts',
  drive: 'Drive',
  spreadsheets: 'Spreadsheets',
  gmail: 'Gmail',
};

export default function GoogleAPIStatus() {
  const { loggedInUser, features } = useUserSettings();

  const adminApiSettings: Record<string, string> = useMemo(
    () =>
      isFeatureEnabled('driveLabelsQ4', features)
        ? { ...ADMIN_API_SETTINGS, ...DRIVE_LABELS_API_SETTINGS }
        : ADMIN_API_SETTINGS,
    [features]
  );

  const [autoCompleteResponse, setAutoCompleteResponse] =
    useState(loggedInUser);

  const queryClient = useQueryClient();

  const results = useQueries({
    queries: Object.keys(adminApiSettings).map((key) => ({
      queryKey: [UserKeys.AdminApiSettings, key],
      queryFn: () => doApiCheck(key, null),
    })),
  });

  const userApiResults = useQueries({
    queries: Object.keys(USER_API_SETTINGS).map((key) => ({
      queryKey: [UserKeys.UserApiSettings, key, autoCompleteResponse],
      queryFn: () => doApiCheck(key, autoCompleteResponse),
      enabled: !!autoCompleteResponse,
    })),
  });

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Google API Status" />
      <Box sx={{ px: 1 }}>
        <Stack flexDirection="column">
          <Stack>
            <Divider variant="middle" textAlign="left">
              Admin APIs
            </Divider>
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="caption" pl={2} width="75%">
                This will run a check against the listed APIs for your domain
                using the current gPanel Admin.
              </Typography>

              <Button
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: [UserKeys.AdminApiSettings],
                  })
                }
                variant="contained"
                sx={{ height: 40 }}
              >
                Check All
              </Button>
            </Stack>
            <Stack width={400} ml={6}>
              {Object.keys(adminApiSettings).map((key, index) => (
                <span key={key}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography>{adminApiSettings[key]}</Typography>
                    <Stack direction="row" alignItems="center">
                      {results[index].isLoading || results[index].isFetching ? (
                        <CircularProgress size={24} />
                      ) : results[index].data ? (
                        <Tooltip arrow title="Check Successful">
                          <Check color="success" />
                        </Tooltip>
                      ) : (
                        <Tooltip arrow title="Check Failed">
                          <Error color="error" />
                        </Tooltip>
                      )}
                      <Tooltip arrow title="Check API">
                        <IconButton
                          onClick={() =>
                            queryClient.invalidateQueries({
                              queryKey: [UserKeys.AdminApiSettings, key],
                            })
                          }
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </span>
              ))}
            </Stack>
          </Stack>
          <Stack>
            <Divider variant="middle" textAlign="left" sx={{ my: 2 }}>
              User APIs
            </Divider>
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="caption" pl={2} width="75%">
                This will run a check against the listed APIs for the provided
                user in your domain.
              </Typography>
              <Button
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: [UserKeys.UserApiSettings],
                  })
                }
                variant="contained"
                sx={{ height: 40 }}
                disabled={!autoCompleteResponse}
              >
                Check All
              </Button>
            </Stack>
            <Box sx={{ width: 400, ml: 4, mb: 1 }}>
              {autoCompleteResponse ? (
                <AvatarChip
                  email={autoCompleteResponse}
                  type="User"
                  onDelete={() => setAutoCompleteResponse('')}
                />
              ) : (
                <UsersAutocomplete
                  autocompleteValue={autoCompleteResponse}
                  setAutoCompleteValue={setAutoCompleteResponse}
                />
              )}
            </Box>
            <Stack width={400} ml={6}>
              {Object.keys(USER_API_SETTINGS).map((key, index) => (
                <span key={key}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography>
                      {USER_API_SETTINGS[key as keyof typeof USER_API_SETTINGS]}
                    </Typography>
                    <Stack direction="row" alignItems="center">
                      {userApiResults[index].isLoading ||
                      userApiResults[index].isFetching ? (
                        <CircularProgress size={24} />
                      ) : userApiResults[index].data ? (
                        <Tooltip arrow title="Check Successful">
                          <Check color="success" />
                        </Tooltip>
                      ) : autoCompleteResponse !== '' ? (
                        <Tooltip arrow title="Check Failed">
                          <Error color="error" />
                        </Tooltip>
                      ) : (
                        <Tooltip arrow title="User not selected">
                          <WarningAmber color="warning" />
                        </Tooltip>
                      )}
                      <Tooltip arrow title="Check API">
                        <IconButton
                          onClick={() =>
                            queryClient.invalidateQueries({
                              queryKey: [
                                UserKeys.UserApiSettings,
                                key,
                                autoCompleteResponse,
                              ],
                            })
                          }
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </span>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
